/**
 * 🔴 W72 STEP 4 — PROFILE, ONE TAB FOR "WHO IS THIS" (Proposal 4, his yes): rows on the ground under small-caps
 * headings, the engine's inputs in the engine's order (his 2026-09-02 rule for the setup, applied to the coach's
 * side) — TRAINING · GOALS · INJURIES AND LIMITS · EQUIPMENT · NEXT WEEK · the client card · `End coaching…`.
 * Every sentence here is pure and executed by `profileTab.test.ts`; `routes/ClientProfile.tsx` only places them.
 *
 * ⚠️ WHAT IS DELIBERATELY NOT SAID: a training LEVEL. The phone's level is `TrainingHistory.effectiveMonths` plus the
 * unanswered-default rule (two testers still carry the silent 18-month default), and there is no proven web twin
 * of it — printing "Intermediate" here would be a second, unproven reader of the one question this app keeps getting
 * wrong. So the row states what the athlete ANSWERED (a start date, "never trained", a break), or "Not set".
 */

import { equipmentName } from "./equipmentLibrary";
import { mainGoalTitle } from "./mainGoals";
import { longDate } from "./weekLine";

export type Row = { readonly label: string; readonly value: string | null };

export type TrainingInput = {
  readonly trainingDaysPerWeek: number | null;
  readonly sessionLengthMinutes: number | null;
  readonly unitSystem: string | null;
  readonly trainingStartDate: string | null;
  readonly neverTrained: boolean | null;
  readonly trainingBreakMonths: number | null;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Training since Jul 2022 · a 30-month break", "Never trained", or null ("Not set"). */
export function trainingHistoryLine(t: TrainingInput): string | null {
  if (t.neverTrained === true) return "Never trained";
  const m = /^(\d{4})-(\d{2})/.exec(t.trainingStartDate ?? "");
  if (m === null) return null;
  const since = `Training since ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
  const away = t.trainingBreakMonths ?? 0;
  return away > 0 ? `${since} · a ${away}-month break` : since;
}

export function trainingRows(t: TrainingInput): Row[] {
  return [
    { label: "Days a week", value: t.trainingDaysPerWeek === null ? null : String(t.trainingDaysPerWeek) },
    { label: "Session length", value: t.sessionLengthMinutes === null ? null : `${t.sessionLengthMinutes} min` },
    { label: "Training history", value: trainingHistoryLine(t) },
    { label: "Units", value: t.unitSystem === "imperial" ? "Pounds and inches" : t.unitSystem === "metric" ? "Kilograms and centimetres" : null },
  ];
}

/** The ranked mains as the phone names them (an unknown slug is dropped, never shown as jargon). */
export function rankedMains(slugs: readonly string[]): string[] {
  return slugs.map((s) => mainGoalTitle(s)).filter((t): t is string => t !== null);
}

/** The goals the engine reads (`profiles.goals`), in their stored order, trimmed and de-duplicated case-blind. */
export function specificGoals(goals: readonly string[]): string[] {
  const seen = new Set<string>();
  return goals.map((g) => g.trim()).filter((g) => g !== "" && !seen.has(g.toLowerCase()) && (seen.add(g.toLowerCase()), true));
}

/** The first `n` equipment names in the vocabulary's words, and how many more. NEVER a raw key (2026-10-10: an unknown
 *  key printed as itself, `snake_case` and all) — `equipmentName`, the one reader: a coach's own piece by its slug, any
 *  other key through `vocabularyLabel`. */
export function equipmentLine(selected: readonly string[], assumed: boolean, n = 8): { shown: string[]; more: number; note: string | null } {
  const names = selected.map((k) => equipmentName(k, []));
  return {
    shown: names.slice(0, n),
    more: Math.max(0, names.length - n),
    note: selected.length === 0 ? "None recorded"
      : assumed ? "The whole catalogue, assumed at setup — they rule pieces out on the swap page" : null,
  };
}

/** The client card's lines. `since` is the day they accepted (`accepted_at`), never the invitation. */
export function clientCardLines(c: { email: string | null; acceptedAt: string | null; lastTrainedDayKey: string | null }): Row[] {
  return [
    { label: "Email", value: c.email },
    { label: "Coached since", value: c.acceptedAt === null ? null : longDate(c.acceptedAt.slice(0, 10)) },
    { label: "Last trained", value: c.lastTrainedDayKey === null ? null : longDate(c.lastTrainedDayKey) },
  ];
}

/** The latest day key in a set of trained days, or null. */
export function lastTrained(days: ReadonlySet<string>): string | null {
  let best: string | null = null;
  for (const d of days) if (best === null || d > best) best = d;
  return best;
}
